import { Flex } from '@dnb/eufemia';
import type { AppWithEntitlementsDto } from '@portals/shared/portal/AppDto';
import { ClientDto } from '@portals/shared/portal/ClientDto';
import { TeamDetailsDto } from '@portals/shared/portal/TeamDto';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import useSWR from 'swr';

import ModalPage from '@/components/ModalPage';
import useAuth from '@/hooks/useAuth';
import useReturnTo from '@/hooks/useReturnTo';
import { useUser } from '@/hooks/useUser';

import { privilegeGroup } from '../../../Credential/components/ClientCard';
import AccessesSection from './components/AccessesSection';
import DetailsSection from './components/DetailsSection';
import SectionSkeleton from './components/SectionSkeleton';
import SigningKeysCard from './components/SigningKeysCard';

import { GridContainer, GridLabel, GridValue } from '../../index.styles';

type EditableSection = 'accesses' | 'details';

function getScopesFromClientAndApp(
  client: ClientDto,
  app: AppWithEntitlementsDto,
): {
  initialScopes: string[];
  initialExtraScopes: string[];
} {
  const allAppEntitlements = app?.currentApiAndScopeEntitlements.flatMap((i) => i.scopeEntitlements) ?? [];
  const entitlementsInEnvironment = new Set(
    allAppEntitlements.filter((entitlement) => entitlement.liveMode === client.liveMode).map((i) => i.scopeName),
  );

  const allClientScopes = client?.scopes ?? [];
  const initialScopes = allClientScopes.filter((scope) => entitlementsInEnvironment.has(scope));
  const initialExtraScopes = allClientScopes.filter((scope) => !initialScopes.includes(scope));
  return { initialScopes, initialExtraScopes };
}

export default function UpdateClientForm() {
  const [editing, setEditing] = useState<EditableSection | null>(null);

  const [returnTo, , clearReturnTo] = useReturnTo();
  const { teamId, appId, clientId } = useParams();
  const navigate = useNavigate();
  const { user } = useUser();
  const { roles } = useAuth();

  const { data: app, isValidating: appLoading } = useSWR<AppWithEntitlementsDto>(`/apps/${appId}`);

  const { data: team, isValidating: teamLoading } = useSWR<TeamDetailsDto>(teamId ? `/team/${teamId}` : null);

  const {
    data: client,
    isValidating: clientLoading,
    mutate: mutateClient,
  } = useSWR<ClientDto>(appId && clientId ? `/apps/${appId}/clients/${clientId}` : null);

  const hasClientManagementGrp = useMemo(() => {
    return roles.includes(privilegeGroup.id);
  }, [roles]);

  const isAdmin = useMemo(() => {
    if (app && app.owner.type !== 'team') {
      return true;
    }
    return Boolean(team && team.members.some((member) => member.email === user?.email && member.isAdmin));
  }, [team, app, user]);

  const hasPrivilegeToEditClient = useMemo(() => {
    return isAdmin && hasClientManagementGrp;
  }, [isAdmin, hasClientManagementGrp]);

  const availableApiAndScopeEntitlements = useMemo(() => app?.currentApiAndScopeEntitlements ?? [], [app]);

  const hasScopeEntitlements = useMemo(
    () => availableApiAndScopeEntitlements.some(({ scopeEntitlements }) => scopeEntitlements.length > 0),
    [availableApiAndScopeEntitlements],
  );

  const scopeState = useMemo(() => {
    if (!client || !app) {
      return undefined;
    }
    const { initialScopes, initialExtraScopes } = getScopesFromClientAndApp(client, app);
    const selectedScopes = (client.scopes ?? []).reduce(
      (acc, scope) => {
        acc[scope] = true;
        return acc;
      },
      {} as Record<string, boolean>,
    );
    return { initialExtraScopes, initialScopes, selectedScopes };
  }, [app, client]);

  const handleBack = useCallback(() => {
    if (returnTo) {
      navigate(-1);
      clearReturnTo();
      return;
    }
    navigate('/');
    clearReturnTo();
  }, [returnTo, navigate, clearReturnTo]);

  const handleEditDetails = useCallback(() => setEditing('details'), []);
  const handleEditAccesses = useCallback(() => setEditing('accesses'), []);
  const handleEditEnd = useCallback(() => setEditing(null), []);
  const handleSaved = useCallback(() => mutateClient(), [mutateClient]);

  return (
    <ModalPage
      onClose={handleBack}
      skeleton={appLoading || clientLoading || teamLoading}
      subtitle={
        <GridContainer>
          <GridLabel>App name</GridLabel>
          <GridValue>{app?.name}</GridValue>
          <GridLabel>Description</GridLabel>
          <GridValue>{app?.description ?? ''}</GridValue>
          <GridLabel>Client ID</GridLabel>
          <GridValue>{client?.id}</GridValue>
          <GridLabel>CIAM application ID</GridLabel>
          <GridValue>{client?.ciamAppId}</GridValue>
        </GridContainer>
      }
      title={hasPrivilegeToEditClient ? 'Update client' : 'Client details'}
    >
      <Flex.Stack gap="medium" wrap={false}>
        {!appId || !clientId || !client || !scopeState ? (
          <SectionSkeleton title="Details" />
        ) : (
          <DetailsSection
            appId={appId}
            canEdit={hasPrivilegeToEditClient}
            clientId={clientId}
            description={client.description ?? ''}
            editDisabled={editing === 'accesses'}
            liveMode={client.liveMode}
            name={client.name ?? ''}
            onEdit={handleEditDetails}
            onEditEnd={handleEditEnd}
            onSaved={handleSaved}
            scopes={client.scopes ?? []}
          />
        )}
        {!appId || !clientId || !client || !scopeState ? (
          <SectionSkeleton title="Accesses" />
        ) : (
          <AccessesSection
            appId={appId}
            availableApiAndScopeEntitlements={availableApiAndScopeEntitlements}
            canEdit={hasPrivilegeToEditClient}
            clientId={clientId}
            description={client.description ?? ''}
            editDisabled={editing === 'details'}
            extraScopesInClient={scopeState.initialExtraScopes}
            initialScopes={scopeState.initialScopes}
            liveMode={client.liveMode}
            name={client.name ?? ''}
            onEdit={handleEditAccesses}
            onEditEnd={handleEditEnd}
            onSaved={handleSaved}
            selectedScopes={scopeState.selectedScopes}
          />
        )}
        {appId && clientId && (
          <SigningKeysCard
            appId={appId}
            clientId={clientId}
            hasClientManagementGrp={hasClientManagementGrp}
            hasPrivilegeToEditClient={hasPrivilegeToEditClient}
            hasScopeEntitlements={hasScopeEntitlements}
            isAdmin={isAdmin}
            liveMode={client?.liveMode ?? false}
          />
        )}
      </Flex.Stack>
    </ModalPage>
  );
}
